import React from 'react'
import EyeIcon from 'assets/icons/eye-2-line-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import BlockIcon from 'assets/icons/block-icon'
import DelistIcon from 'assets/icons/delist-icon'
import ViewIcon from 'assets/icons/view-icon'
import SupersedeIcon from 'assets/icons/supersede-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import RevertIcon from 'assets/icons/revert-icon'
import EditIcon from 'assets/icons/edit-icon'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_NOT_ASSESSED_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID,
  SITE_STATUS_SUPERSEDED_ID,
} from '../siteStatuses'
import {
  getSupersedeOrUndoMenuTitle,
  removeSupersedeIfKbaEditOnly,
  removeSupersedeIfKbaStatusTooLow,
  withSupersedeModalIntent,
} from '../../utils/supersedeMenuHelpers'
import { BIRDLIFE_SECRETARIAT_ROLE_ID } from '../roles'
import { canSecretariatRemoveDraft } from '../../utils/draftRemovalPermissions'
import {
  canBirdLifeSecretariatConfirmSite,
  canBirdLifeSecretariatDelistOrSupersede,
  canBirdLifeSecretariatSupersedeOrUndoSupersed,
} from 'utils/siteIbaAccess'
import { getEditOnlyContext } from '../../utils/editOnlyContext'
import {
  createNavigateReviewDetails,
  createNavigateViewSiteWithAllHistories,
  handleCreateMonitoring,
  handleGetMonitoring,
} from './navigationHandlers'
import { buildMonitoringEvaluationUrl } from 'routing/constants'

function isBirdLifeSecretariatProfile(profile) {
  return profile?.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID
}

function removeDelistAssessmentByRules(site, actionList) {
  if (site.historyId && !site.delist) {
    const delistIndex = actionList.findIndex((item) => item.title === 'delistAssessment')
    if (delistIndex !== -1) {
      actionList.splice(delistIndex, 1)
    }
  }
  if (site.delist == undefined) {
    const delistIndex = actionList.findIndex((item) => item.title === 'delistAssessment')
    if (delistIndex !== -1) {
      actionList.splice(delistIndex, 1)
    }
  }
}

const getMonitoringActionMenuItems = ({
  site,
  profile,
  history,
  isMySite,
  setMonitoringForDeleting,
  setMonitoringForReverting
}) => {
  const monitoringActions = []

  // View monitoring evaluation
  const siteEvaluations = Array.isArray(site?.monitoring)
    ? [...site.monitoring].sort((a, b) => Number(b.id) - Number(a.id))
    : []
  const confirmedMonitoring = siteEvaluations.find(
    (entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID
  )
  
  if (confirmedMonitoring) {
    monitoringActions.push({
      icon: <EyeIcon />,
      title: 'viewMonitoringEvaluation',
      disabled: false,
      onClick: () => {
        handleGetMonitoring(site.id, confirmedMonitoring.id, history)
      }
    })
  }

  // Edit/Add monitoring evaluation
  const siteEvaluationsForEdit = site?.monitoring
  let targetEvaluation = null
  let lockedByOtherUser = false
  let alreadyEvaluated
  
  if (Array.isArray(siteEvaluationsForEdit)) {
    targetEvaluation = siteEvaluationsForEdit.find((entry) => {
      return entry && entry.monitor_users_id === profile.id && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })
    lockedByOtherUser = siteEvaluationsForEdit.some((entry) => {
      const isDraft = entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
      const isOwner = entry && entry.monitor_users_id === profile.id
      const isUserCoProposer = site?.isUserCoProposer
      return isDraft && (!isOwner && !isUserCoProposer)
    })
    alreadyEvaluated = siteEvaluationsForEdit.some((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
  }
  
  const isBirdLifeSecretariatTargetEvaluation =
    Array.isArray(siteEvaluationsForEdit) &&
    siteEvaluationsForEdit.find((entry) => {
      return entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })

  if (isBirdLifeSecretariatTargetEvaluation) {
    monitoringActions.push({
      icon: <EditIcon />,
      title: 'editMonitoringEvaluation',
      disabled: false,
        onClick: () => {
          localStorage.setItem('isEdit', 'false')
          const monitoringId = isBirdLifeSecretariatTargetEvaluation.id || isBirdLifeSecretariatTargetEvaluation.monitoringId
          history(buildMonitoringEvaluationUrl(site.id, monitoringId))
        }
      })
  }
  

  // Remove monitoring
  const siteEvaluationsForRemove = site?.monitoring
  let targetEvaluationForRemove = null
  if (Array.isArray(siteEvaluationsForRemove)) {
    targetEvaluationForRemove = siteEvaluationsForRemove.find((entry) => {
      return entry && entry.monitor_users_id === profile.id && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })
  }
  
  if (isMySite && targetEvaluationForRemove) {
    monitoringActions.push({
      icon: <RemoveIcon />,
      title: 'removeMonitoring',
      disabled: false,
      onClick: () => {
        setMonitoringForDeleting(targetEvaluationForRemove)
      }
    })
  }

  // Revert submission (BirdLife Secretariat only)
  let targetEvaluationForRevert = null
  if (siteEvaluations.length > 0) {
    targetEvaluationForRevert = siteEvaluations.find((entry) => {
      return entry && entry.monitoring_status === SITE_STATUS_CONFIRMED_ID
    })
  }
  const isBirdLifeSecretariatTargetEvaluationForRevert =
    siteEvaluations.length > 0 &&
    siteEvaluations.find((entry) => {
      return entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })
  
  if (targetEvaluationForRevert && !isBirdLifeSecretariatTargetEvaluationForRevert) {
    monitoringActions.push({
      icon: <RevertIcon />,
      title: 'revertSubmission',
      disabled: false,
      onClick: () => {
        setMonitoringForReverting(targetEvaluationForRevert)
      }
    })
  }

  return monitoringActions
}

function buildNominatedOrSubmittedSecretariatItems(ctx) {
  const {
    site,
    history,
    setSiteToPublish,
    setSiteToDelist,
    approveEditOnlyCallBack,
    profile,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting
  } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const isBirdLifeSecretariat = isBirdLifeSecretariatProfile(profile)

  const actionList = [
    {
      icon: <EyeIcon />,
      title: editOnlySiteStatus ? 'reviewEditOnlySite' : 'review',
      onClick: createNavigateReviewDetails(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatConfirmSite(site, profile)) {
    actionList.push({
      icon: <ConfirmIcon />,
      title: editOnlySiteStatus ? 'approveEditOnlySite' : 'confirmAssessmentBtn',
      onClick: () => {
        if (editOnlySiteStatus) {
          approveEditOnlyCallBack({ ...site, isApproved: true })
          return
        }
        setSiteToPublish({ ...site, isApproved: true })
      },
    })
  }

  actionList.push({
    icon: <BlockIcon />,
    title: editOnlySiteStatus ? 'returnToEditOnly' : 'rejectAssessmentBtn',
    onClick: () => {
      if (editOnlySiteStatus) {
        approveEditOnlyCallBack({ ...site, isApproved: false })
        return
      }
      setSiteToPublish({ ...site, isApproved: false })
    },
  })

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatDelistOrSupersede(site, profile)) {
    actionList.push({
      icon: <DelistIcon />,
      title: 'delistAssessment',
      onClick: () => {
        setSiteToDelist(site)
      },
    })
  }

  const monitoringActions = getMonitoringActionMenuItems({
    site,
    profile,
    history,
    isMySite,
    handleGetMonitoring,
    handleCreateMonitoring,
    setMonitoringForDeleting,
    setMonitoringForReverting
  })
 
  // Spread monitoring actions into the main action list
  actionList.push(...monitoringActions)
  removeDelistAssessmentByRules(site, actionList)
  return actionList
}

function buildDelistedSecretariatItems(ctx) {
  const { site, history, changeStatusCallback, profile, isMySite, setMonitoringForDeleting, setMonitoringForReverting } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const isBirdLifeSecretariat = isBirdLifeSecretariatProfile(profile)

  const delistedActions = [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatSupersedeOrUndoSupersed(site, profile)) {
    delistedActions.push({
      icon: <SupersedeIcon />,
      title: getSupersedeOrUndoMenuTitle(site),
      onClick: () => {
        changeStatusCallback(withSupersedeModalIntent(site, { isApproved: true }))
      },
    })
  }
  const monitoringActions = getMonitoringActionMenuItems({
    site,
    profile,
    history,
    isMySite,
    handleGetMonitoring,
    handleCreateMonitoring,
    setMonitoringForDeleting,
    setMonitoringForReverting
  })
 
  // Spread monitoring actions into the main action list
  delistedActions.push(...monitoringActions)

  return delistedActions
}

function buildNonConfirmedNonDraftSecretariatView(ctx) {
  const { site, history, profile, isMySite, setMonitoringForDeleting, setMonitoringForReverting } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const viewOnlyActions = [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]
  const monitoringActions = getMonitoringActionMenuItems({
    site,
    profile,
    history,
    isMySite,
    handleGetMonitoring,
    handleCreateMonitoring,
    setMonitoringForDeleting,
    setMonitoringForReverting
  })
 
  // Spread monitoring actions into the main action list
  viewOnlyActions.push(...monitoringActions)
  
  return viewOnlyActions
}

/**
 * In-flight assessment statuses (e.g. proposed, RFP review): secretariat table shows view-only.
 * Does not qualify / not assessed are final KBA outcomes (ND in superseding permutations): use default menu so KBA Sec can supersede per business rules; BirdLife remains gated in buildDefaultSecretariatDropdown.
 */
function shouldUseNonConfirmedViewOnly(site) {
  if (!site.SiteStatus) {
    return false
  }
  const id = site.SiteStatus.id
  if (id === SITE_STATUS_DOES_NOT_QUALIFY || id === SITE_STATUS_NOT_ASSESSED_ID) {
    return false
  }
  return (
    id !== SITE_STATUS_CONFIRMED_ID &&
    id !== SITE_STATUS_SUPERSEDED_ID &&
    id !== SITE_STATUS_DRAFT_ID &&
    id !== SITE_STATUS_EDIT_ONLY_ID
  )
}

function buildDefaultSecretariatDropdown(ctx) {
  const { site, history, changeStatusCallback, setSiteForDeleting, profile, isMySite, setMonitoringForDeleting, setMonitoringForReverting } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const isBirdLifeSecretariat = isBirdLifeSecretariatProfile(profile)

  const secretariatDropdownItems = [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatSupersedeOrUndoSupersed(site, profile)) {
    secretariatDropdownItems.push({
      icon: <SupersedeIcon />,
      title: getSupersedeOrUndoMenuTitle(site),
      onClick: () => {
        changeStatusCallback(withSupersedeModalIntent(site))
      },
    })
  }

  removeSupersedeIfKbaStatusTooLow(secretariatDropdownItems, site)
  removeSupersedeIfKbaEditOnly(secretariatDropdownItems, site)

  if (canSecretariatRemoveDraft(profile, site)) {
    secretariatDropdownItems.push({
      icon: <RemoveIcon />,
      title: 'Remove',
      onClick: () => {
        setSiteForDeleting(site)
      },
    })
  }
  const monitoringActions = getMonitoringActionMenuItems({
    site,
    profile,
    history,
    isMySite,
    handleGetMonitoring,
    handleCreateMonitoring,
    setMonitoringForDeleting,
    setMonitoringForReverting
  })
 
  // Spread monitoring actions into the main action list
  secretariatDropdownItems.push(...monitoringActions)

  return secretariatDropdownItems
}

export function buildBirdlifeSecretariatAndKbaSecretariatMenuItems(ctx) {
  const { site } = ctx

  if (
    site.SiteStatus?.id === SITE_STATUS_NOMINATED_ID ||
    site.SiteStatus?.id === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  ) {
    return buildNominatedOrSubmittedSecretariatItems(ctx)
  }
  if (site.SiteStatus?.id === SITE_STATUS_DELISTED_ID) {
    return buildDelistedSecretariatItems(ctx)
  }
  if (shouldUseNonConfirmedViewOnly(site)) {
    return buildNonConfirmedNonDraftSecretariatView(ctx)
  }
  return buildDefaultSecretariatDropdown(ctx)
}

export function parseSecretariatMenuArgs(args) {
  const [
    site,
    history,
    changeStatusCallback,
    setSiteToPublish,
    setSiteToDelist,
    ,
    setSiteForDeleting,
    ,
    ,
    approveEditOnlyCallBack,
    profile,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting
  ] = args

  return {
    site,
    history,
    changeStatusCallback,
    setSiteToPublish,
    setSiteToDelist,
    setSiteForDeleting,
    approveEditOnlyCallBack,
    profile,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting
  }
}

export function getBirdlifeSecretariatAndKbaSecretariatActionMenuItems(...args) {
  return buildBirdlifeSecretariatAndKbaSecretariatMenuItems(parseSecretariatMenuArgs(args))
}
